import http from "services/http.service";
import Promisable from "services/promisable.service";

export type PrivateAssetType =
  | "auto"
  | "document"
  | "invoice"
  | "quotation"
  | "contract"
  | "profile"
  | "pickup_sign"
  | "private";

/**
 * Opens a private file with a fresh presigned URL (24h).
 * Works for old CloudFront links and new S3 keys stored in MongoDB.
 */
export async function openPrivateFile(
  storedUrl: string | undefined | null,
  assetType: PrivateAssetType = "auto",
  options: { download?: boolean } = {}
) {
  if (!storedUrl) return;

  http.setJWT();
  const [success]: any = await Promisable.asPromise(
    http.post(
      "/file/presigned-get",
      { url: storedUrl, assetType },
      { silentToast: true } as any
    )
  );

  const download_url = success?.data?.data?.download_url;
  if (!download_url) return;

  const forceDownload = options.download ?? assetType === "pickup_sign";

  if (forceDownload) {
    const link = document.createElement("a");
    link.href = download_url;
    link.rel = "noopener noreferrer";
    link.download = "";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  window.open(download_url, "_blank", "noopener,noreferrer");
}

/** Pickup sign: fresh presigned link, saves PDF to disk (does not open in tab). */
export async function downloadPickupSign(storedUrl: string | undefined | null) {
  return openPrivateFile(storedUrl, "pickup_sign", { download: true });
}

/** Invoice download — always requests a new 24h link on click. */
export async function openInvoiceDownload(
  invoiceId: string,
  fallbackUrl?: string
) {
  http.setJWT();
  const [success]: any = await Promisable.asPromise(
    http.get(`/invoices/${invoiceId}/download-url`, { silentToast: true } as any)
  );

  const download_url = success?.data?.data?.download_url;
  if (download_url) {
    window.open(download_url, "_blank", "noopener,noreferrer");
    return;
  }

  if (fallbackUrl) {
    await openPrivateFile(fallbackUrl, "invoice");
  }
}
